import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from 'react';
import { PlusOutlined, CopyOutlined, HolderOutlined } from '@ant-design/icons';
import { Button, Input, Modal, message, Tooltip } from 'antd';
import { DragDropContext, Draggable } from 'react-beautiful-dnd';
import StrictModeDroppable from './components/StrictModeDroppable';
import FlowComponentEditor from './components/FlowComponentEditor';
import AddComponentModal from './components/AddComponentModal';
import { normalizeOptionDataSource } from '../utils/optionUtils';
import CustomDelete from '../../../../../../assets/customSVG/CustomDelete';
import { AttributeList } from '../../../../../campaigns/api/sequence';

/**
 * FlowJSONEditor Component
 *
 * A comprehensive editor for WhatsApp Flow JSON with drag-and-drop support
 * Follows Meta's official WhatsApp Flows specifications
 *
 * @param {Object} props
 * @param {Object} props.initialFlowJSON - Initial flow JSON structure
 * @param {Function} props.onFlowChange - Callback when flow JSON changes
 */
const FORM_ALLOWED_COMPONENT_TYPES = [
  'TextHeading',
  'TextSubheading',
  'TextBody',
  'TextCaption',
  'Image',
  'TextInput',
  'TextArea',
  'DatePicker',
  'RadioButtonsGroup',
  'CheckboxGroup',
  'Dropdown',
  'OptIn',
  'Footer',
];

const ALPHA_CHARACTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

const generateAlphaString = (length = 8) => {
  let result = '';
  for (let index = 0; index < length; index += 1) {
    result += ALPHA_CHARACTERS.charAt(
      Math.floor(Math.random() * ALPHA_CHARACTERS.length),
    );
  }
  return result;
};

const createUniqueAlphaId = (prefix, existingIds = new Set(), length = 8) => {
  const normalizedPrefix = prefix.endsWith('_') ? prefix.slice(0, -1) : prefix;
  const taken = new Set(existingIds);
  let attempts = 0;

  while (attempts < 50) {
    const candidate = `${normalizedPrefix}_${generateAlphaString(length)}`;
    if (!taken.has(candidate)) {
      return candidate;
    }
    attempts += 1;
  }

  return `${normalizedPrefix}_${generateAlphaString(length + 2)}`;
};

const stripFormVisible = (component) => {
  if (!component || component.type !== 'Form') {
    return component;
  }

  const nextComponent = { ...component };
  if (nextComponent.visible !== undefined) {
    delete nextComponent.visible;
  }
  return nextComponent;
};

function getDefaultComponentProps(componentType) {
  const baseProps = {};

  switch (componentType) {
    case 'TextHeading':
      return { ...baseProps, type: 'TextHeading', text: 'Large Heading' };

    case 'TextSubheading':
      return { ...baseProps, type: 'TextSubheading', text: 'Small Heading' };

    case 'TextCaption':
      return { ...baseProps, type: 'TextCaption', text: 'Caption' };

    case 'TextBody':
      return { ...baseProps, type: 'TextBody', text: 'Body text' };

    case 'Image':
      return {
        ...baseProps,
        type: 'Image',
        src: '',
        'scale-type': 'contain',
      };

    case 'TextInput': {
      const timestamp = Date.now();
      return {
        ...baseProps,
        type: 'TextInput',
        name: `Short Answer ${timestamp}`, // Will be updated when user enters label
        label: 'Short Answer',
        'input-type': 'text',
        required: false,
        'min-chars': 1,
        'max-chars': 35,
      };
    }

    case 'TextArea': {
      const timestamp = Date.now();
      return {
        ...baseProps,
        type: 'TextArea',
        name: `Paragraph ${timestamp}`, // Will be updated when user enters label
        label: 'Paragraph',
        required: false,
        'max-length': 1000,
      };
    }

    case 'DatePicker': {
      const timestamp = Date.now();
      return {
        ...baseProps,
        type: 'DatePicker',
        name: `Date Picker ${timestamp}`, // Will be updated when user enters label
        label: 'Date Picker',
        required: false,
      };
    }

    case 'CheckboxGroup': {
      const timestamp = Date.now();
      return {
        ...baseProps,
        type: 'CheckboxGroup',
        name: `Choose that apply ${timestamp}`, // Will be updated when user enters label
        label: 'Choose that apply',
        required: false,
        'data-source': [],
      };
    }

    case 'RadioButtonsGroup': {
      const timestamp = Date.now();
      return {
        ...baseProps,
        type: 'RadioButtonsGroup',
        name: `Select one option ${timestamp}`, // Will be updated when user enters label
        label: 'Select one option',
        required: false,
        'data-source': [],
      };
    }

    case 'Form': {
      const footerComponent = {
        type: 'Footer',
        label: 'Submit',
        'on-click-action': {
          name: 'complete',
          payload: {},
        },
      };

      return {
        ...baseProps,
        type: 'Form',
        name: `form_${generateAlphaString(6)}`,
        children: [footerComponent],
      };
    }

    case 'Dropdown': {
      const timestamp = Date.now();
      return {
        ...baseProps,
        type: 'Dropdown',
        name: `Select ${timestamp}`, // Will be updated when user enters label
        label: 'Select',
        required: false,
        'data-source': [],
      };
    }

    case 'OptIn': {
      const timestamp = Date.now();
      return {
        ...baseProps,
        type: 'OptIn',
        name: `Label ${timestamp}`, // Will be updated when user enters label
        label: 'Label',
        required: true,
      };
    }

    case 'Footer': {
      return {
        type: 'Footer',
        label: 'Continue',
        'on-click-action': {
          name: 'navigate',
          next: { type: 'screen', name: '' },
          payload: {},
        },
      };
    }

    default:
      return baseProps;
  }
}

function getDefaultFlowJSON() {
  return {
    version: '7.2',
    screens: [
      {
        id: 'SCREEN_ONE',
        title: 'Question 1 of 3',
        terminal: true,
        data: {},
        layout: {
          type: 'SingleColumnLayout',
          children: [getDefaultComponentProps('Form')],
        },
      },
    ],
  };
}

function ensureScreenFormStructure(screens, screen) {
  if (!screen || typeof screen !== 'object') return screen;

  const layout = screen.layout || {};
  const children = Array.isArray(layout.children)
    ? layout.children.filter(Boolean)
    : [];

  if (isReadMoreScreen(screens, screen, true)) {
    let normalizedChildren = children;

    if (children.length === 1 && children[0]?.type === 'Form') {
      const formChildren = Array.isArray(children[0].children)
        ? children[0].children.filter(Boolean)
        : [];
      normalizedChildren = formChildren;
    }

    const filteredChildren = normalizedChildren.filter(
      (child) => child && child.type !== 'Footer',
    );

    return {
      ...screen,
      layout: {
        ...layout,
        children: filteredChildren,
      },
    };
  }

  if (children.length === 1 && children[0]?.type === 'Form') {
    const formComponent = stripFormVisible(children[0]);
    const formChildren = Array.isArray(formComponent.children)
      ? formComponent.children.filter(Boolean)
      : [];
    const hasFooter = formChildren.some((child) => child?.type === 'Footer');

    const nextChildren = hasFooter
      ? formChildren
      : [...formChildren, getDefaultComponentProps('Footer')];

    return {
      ...screen,
      layout: {
        ...layout,
        children: [
          {
            ...formComponent,
            children: nextChildren,
          },
        ],
      },
    };
  }

  let baseForm = children.find((child) => child?.type === 'Form') || null;
  const orderedChildren = [];
  let footer = null;

  children.forEach((child) => {
    if (!child || typeof child !== 'object') return;

    if (child.type === 'Form') {
      if (!baseForm) {
        baseForm = child;
      }

      const nestedChildren = Array.isArray(child.children)
        ? child.children.filter(Boolean)
        : [];

      nestedChildren.forEach((nestedChild) => {
        if (!nestedChild || typeof nestedChild !== 'object') return;
        if (nestedChild.type === 'Footer') {
          if (!footer) {
            footer = nestedChild;
          }
        } else {
          orderedChildren.push(nestedChild);
        }
      });
      return;
    }

    if (child.type === 'Footer') {
      if (!footer) {
        footer = child;
      }
      return;
    }

    orderedChildren.push(child);
  });

  if (!baseForm) {
    baseForm = getDefaultComponentProps('Form');
  }

  baseForm = stripFormVisible(baseForm);

  let finalChildren = orderedChildren;

  if (!footer) {
    footer = getDefaultComponentProps('Footer');
  }

  if (footer) {
    finalChildren = [...orderedChildren, footer];
  }

  return {
    ...screen,
    layout: {
      ...layout,
      children: [
        {
          ...baseForm,
          children: finalChildren,
        },
      ],
    },
  };
}

const normalizeComponentTree = (component) => {
  if (!component || typeof component !== 'object') {
    return component;
  }

  let nextComponent = component;

  if (Array.isArray(component['data-source'])) {
    const normalizedDataSource = normalizeOptionDataSource(
      component['data-source'],
    );
    if (normalizedDataSource !== component['data-source']) {
      nextComponent = {
        ...nextComponent,
        ['data-source']: normalizedDataSource,
      };
    }
  }

  if (
    Array.isArray(nextComponent.children) &&
    nextComponent.children.length > 0
  ) {
    const normalizedChildren = nextComponent.children.map((child) =>
      normalizeComponentTree(child),
    );
    const childrenChanged = normalizedChildren.some(
      (child, idx) => child !== nextComponent.children[idx],
    );

    if (childrenChanged) {
      nextComponent = {
        ...nextComponent,
        children: normalizedChildren,
      };
    }
  }

  return nextComponent;
};

const normalizeScreenComponents = (screen) => {
  if (!screen || typeof screen !== 'object') return screen;

  const layoutChildren = Array.isArray(screen.layout?.children)
    ? screen.layout.children
    : [];

  if (layoutChildren.length === 0) return screen;

  const normalizedChildren = layoutChildren.map((child) =>
    normalizeComponentTree(child),
  );

  const hasChanges = normalizedChildren.some(
    (child, idx) => child !== layoutChildren[idx],
  );

  if (!hasChanges) return screen;

  return {
    ...screen,
    layout: {
      ...screen.layout,
      children: normalizedChildren,
    },
  };
};

const isReadMoreScreen = (screens, screen) => {
  const targetId = typeof screen === 'string' ? screen : screen?.id;

  if (!targetId || !Array.isArray(screens)) return false;

  const traverse = (node) => {
    if (!node) return false;

    // Arrays: check any child
    if (Array.isArray(node)) {
      return node.some(traverse);
    }

    // Objects: check this node, then descend
    if (typeof node === 'object') {
      // ✅ Check for OptIn -> on-click-action.next.name === targetId
      if (
        node.type === 'OptIn' &&
        node['on-click-action']?.next?.name === targetId
      ) {
        return true;
      }

      // Common WhatsApp Flow nesting
      if (node.layout && traverse(node.layout)) return true;
      if (node.children && traverse(node.children)) return true;

      // Be robust: scan any other nested objects/arrays too
      for (const key in node) {
        if (key === 'layout' || key === 'children') continue;
        const val = node[key];
        if (val && (typeof val === 'object' || Array.isArray(val))) {
          if (traverse(val)) return true;
        }
      }
    }

    return false;
  };

  // Look across ALL screens to find an OptIn linking to this targetId
  for (const s of screens) {
    if (traverse(s?.layout)) return true;
  }

  return false;
};

/**
 * Extracts all form field names from a screen's components
 */
const extractFormFields = (screen) => {
  const fields = [];

  const traverse = (component) => {
    if (!component) return;

    // Interactive components with 'name' property
    const interactiveTypes = [
      'TextInput',
      'TextArea',
      'DatePicker',
      'CheckboxGroup',
      'RadioButtonsGroup',
      'Dropdown',
      'OptIn',
    ];

    if (interactiveTypes.includes(component.type) && component.name) {
      fields.push(component.name);
    }

    // Recursively check children (for Forms)
    if (Array.isArray(component.children)) {
      component.children.forEach(traverse);
    }
  };

  // Traverse all layout children
  if (Array.isArray(screen.layout?.children)) {
    screen.layout.children.forEach(traverse);
  }

  return fields;
};

const isNameTakenInForm = (formChildren, candidateName, excludeIndex) => {
  if (!candidateName || !Array.isArray(formChildren)) return false;
  return formChildren.some(
    (child, idx) =>
      idx !== excludeIndex &&
      child &&
      typeof child === 'object' &&
      child.name === candidateName,
  );
};

/**
 * Builds payload for navigate action with screen index prefix
 */
const buildNavigatePayload = (currentScreen, allScreens, currentIndex) => {
  const payload = {};

  // 1. Add all previous screens' data with screen index prefix
  for (let i = 0; i < currentIndex; i++) {
    const prevFields = extractFormFields(allScreens[i]);

    prevFields.forEach((fieldName) => {
      const prefixedKey = `${i}_${fieldName}`;
      payload[prefixedKey] = `\${data.${prefixedKey}}`;
    });
  }

  // 2. Add current screen form fields with screen index prefix
  const currentFields = extractFormFields(currentScreen);
  currentFields.forEach((fieldName) => {
    const prefixedKey = `${currentIndex}_${fieldName}`;
    payload[prefixedKey] = `\${form.${fieldName}}`;
  });

  return payload;
};

/**
 * Builds payload for complete action with screen index prefix
 */
const buildCompletePayload = (currentScreen, allScreens, currentIndex) => {
  const payload = {};

  // 1. Add all previous screens' data with screen index prefix
  for (let i = 0; i < currentIndex; i++) {
    const prevFields = extractFormFields(allScreens[i]);

    prevFields.forEach((fieldName) => {
      const prefixedKey = `${i}_${fieldName}`;
      payload[prefixedKey] = `\${data.${prefixedKey}}`;
    });
  }

  // 2. Add current screen form fields with screen index prefix
  const currentFields = extractFormFields(currentScreen);
  currentFields.forEach((fieldName) => {
    const prefixedKey = `${currentIndex}_${fieldName}`;
    payload[prefixedKey] = `\${form.${fieldName}}`;
  });

  return payload;
};

/**
 * Generates data declarations with screen index prefix
 */
const buildDataDeclaration = (allScreens, currentIndex) => {
  const dataDeclaration = {};

  // Accumulate all fields from previous screens with index prefix
  for (let i = 0; i < currentIndex; i++) {
    const prevScreen = allScreens[i];
    const fields = extractFormFields(prevScreen);

    fields.forEach((fieldName) => {
      const prefixedKey = `${i}_${fieldName}`;
      const component = findComponentByName(prevScreen, fieldName);
      dataDeclaration[prefixedKey] = getDataTypeForComponent(component);
    });
  }

  return dataDeclaration;
};

/**
 * Finds a component by its name attribute
 */
const findComponentByName = (screen, name) => {
  let found = null;

  const traverse = (component) => {
    if (!component) return;
    if (component.name === name) {
      found = component;
      return;
    }
    if (Array.isArray(component.children)) {
      component.children.forEach(traverse);
    }
  };

  if (Array.isArray(screen.layout?.children)) {
    screen.layout.children.forEach(traverse);
  }

  return found;
};

/**
 * Returns proper data type declaration for a component
 */
const getDataTypeForComponent = (component) => {
  if (!component) return { type: 'string', __example__: 'example' };

  switch (component.type) {
    case 'CheckboxGroup':
      return {
        type: 'array',
        items: { type: 'string' },
        __example__: ['option1', 'option2'],
      };

    case 'RadioButtonsGroup':
    case 'Dropdown':
      return {
        type: 'string',
        __example__: 'option1',
      };

    case 'TextInput':
      if (component['input-type'] === 'number') {
        return { type: 'number', __example__: 123 };
      }
      if (component['input-type'] === 'email') {
        return { type: 'string', __example__: 'user@example.com' };
      }
      if (component['input-type'] === 'phone') {
        return { type: 'string', __example__: '+1234567890' };
      }
      if (component['input-type'] === 'passcode') {
        return { type: 'string', __example__: 'Example' };
      }
      if (component['input-type'] === 'password') {
        return { type: 'string', __example__: 'Example' };
      }
      return { type: 'string', __example__: 'example text' };

    case 'TextArea':
      return { type: 'string', __example__: 'example paragraph' };

    case 'DatePicker':
      return { type: 'string', __example__: '2025-01-15' };

    case 'OptIn':
      return { type: 'boolean', __example__: true };

    default:
      return { type: 'string', __example__: 'example' };
  }
};

/**
 * Automatically updates Footer navigation based on screen placement
 * - Last screen (terminal): Footer with 'complete' action, success: true
 * - Other screens: Footer with 'navigate' action, no success property
 */
const autoUpdateFooterNavigation = (screens) => {
  if (!Array.isArray(screens) || screens.length === 0) {
    return screens;
  }

  // Filter out read-more screens for navigation logic
  const mainScreens = screens.filter(
    (screen) => !isReadMoreScreen(screens, screen),
  );

  return screens.map((screen, screenIndex) => {
    // Skip read-more screens
    if (isReadMoreScreen(screens, screen)) {
      return screen;
    }

    const mainScreenIndex = mainScreens.findIndex((s) => s.id === screen.id);
    const isLastMainScreen = mainScreenIndex === mainScreens.length - 1;
    const nextMainScreen =
      mainScreenIndex < mainScreens.length - 1
        ? mainScreens[mainScreenIndex + 1]
        : null;

    const layoutChildren = Array.isArray(screen.layout?.children)
      ? screen.layout.children
      : [];

    // Find Form component
    const formIndex = layoutChildren.findIndex(
      (child) => child?.type === 'Form',
    );

    if (formIndex === -1) {
      return screen;
    }

    const formComponent = layoutChildren[formIndex];
    const formChildren = Array.isArray(formComponent.children)
      ? formComponent.children
      : [];

    // Find Footer in form children
    const footerIndex = formChildren.findIndex(
      (child) => child?.type === 'Footer',
    );

    if (footerIndex === -1) {
      return screen;
    }

    const footer = formChildren[footerIndex];
    let updatedFooter;
    let updatedScreen = { ...screen };

    if (isLastMainScreen) {
      // Last screen: use complete action with ALL accumulated data
      updatedFooter = {
        ...footer,
        'on-click-action': {
          name: 'complete',
          payload: buildCompletePayload(screen, mainScreens, mainScreenIndex),
        },
      };

      updatedScreen = {
        ...updatedScreen,
        terminal: true,
        success: true,
      };
    } else {
      // Non-last screen: use navigate action with current + previous data
      updatedFooter = {
        ...footer,
        'on-click-action': {
          name: 'navigate',
          next: {
            type: 'screen',
            name: nextMainScreen?.id || '',
          },
          payload: buildNavigatePayload(screen, mainScreens, mainScreenIndex),
        },
      };

      // Update next screen's data declaration
      if (nextMainScreen) {
        const nextScreenIndex = screens.findIndex(
          (s) => s.id === nextMainScreen.id,
        );
        if (nextScreenIndex !== -1) {
          const dataDeclaration = buildDataDeclaration(
            mainScreens,
            mainScreenIndex + 1,
          );
          screens[nextScreenIndex] = {
            ...screens[nextScreenIndex],
            data: dataDeclaration,
          };
        }
      }

      updatedScreen = {
        ...updatedScreen,
        terminal: false,
      };
      delete updatedScreen.success;
    }

    // Update form children with new footer
    const updatedFormChildren = formChildren.map((child, idx) =>
      idx === footerIndex ? updatedFooter : child,
    );

    // Update form component
    const updatedFormComponent = {
      ...formComponent,
      children: updatedFormChildren,
    };

    // Update layout children
    const updatedLayoutChildren = layoutChildren.map((child, idx) =>
      idx === formIndex ? updatedFormComponent : child,
    );

    return {
      ...updatedScreen,
      layout: {
        ...updatedScreen.layout,
        children: updatedLayoutChildren,
      },
    };
  });
};

function normalizeFlowJSON(flowJSON) {
  if (!flowJSON || typeof flowJSON !== 'object') {
    return getDefaultFlowJSON();
  }

  let screens = Array.isArray(flowJSON.screens)
    ? flowJSON.screens.map((screen) =>
        ensureScreenFormStructure(flowJSON?.screens, screen),
      )
    : [];

  screens = autoUpdateFooterNavigation(screens);
  screens = screens.map((screen) => normalizeScreenComponents(screen));
  return {
    ...flowJSON,
    screens,
  };
}

const createFormDroppableId = (screenId, formIndex) =>
  `form::${encodeURIComponent(screenId)}::${formIndex}`;

const parseFormDroppableId = (droppableId) => {
  if (!droppableId || !droppableId.startsWith('form::')) return null;
  const parts = droppableId.split('::');
  if (parts.length !== 3) return null;
  const decodedScreenId = decodeURIComponent(parts[1]);
  const formIndex = Number(parts[2]);
  if (Number.isNaN(formIndex)) return null;
  return { screenId: decodedScreenId, formIndex };
};

const createFormChildDraggableId = (screenId, formIndex, childIndex) =>
  `form-item::${encodeURIComponent(screenId)}::${formIndex}::${childIndex}`;

function removeReadMoreScreenReferences(
  screens,
  readMoreScreenId,
  getReadMoreScreenIdFromComponent,
) {
  if (!readMoreScreenId) {
    return { screens, changed: false };
  }

  let hasChanges = false;

  const cleanComponent = (component) => {
    if (!component || typeof component !== 'object') {
      return { component, changed: false };
    }

    let changed = false;
    let nextComponent = component;

    if (
      component.type === 'OptIn' &&
      getReadMoreScreenIdFromComponent(component) === readMoreScreenId
    ) {
      nextComponent = { ...component };
      delete nextComponent['on-click-action'];
      delete nextComponent['read-more-screen-id'];
      changed = true;
    }

    if (Array.isArray(component.children) && component.children.length > 0) {
      let childrenChanged = false;
      const nextChildren = component.children.map((child) => {
        const { component: cleanedChild, changed: childChanged } =
          cleanComponent(child);
        if (childChanged) {
          childrenChanged = true;
        }
        return cleanedChild;
      });

      if (childrenChanged) {
        if (!changed) {
          nextComponent = { ...nextComponent };
          changed = true;
        }
        nextComponent.children = nextChildren;
      }
    }

    return { component: nextComponent, changed };
  };

  const cleanedScreens = (Array.isArray(screens) ? screens : [])
    .map((screen) => {
      if (!screen || typeof screen !== 'object') {
        return screen;
      }

      const layoutChildren = Array.isArray(screen.layout?.children)
        ? screen.layout.children
        : [];

      let screenChanged = false;

      const nextChildren = layoutChildren.map((child) => {
        const { component: cleanedChild, changed } = cleanComponent(child);
        if (changed) {
          screenChanged = true;
        }
        return cleanedChild;
      });

      if (screenChanged) {
        hasChanges = true;
        return {
          ...screen,
          layout: {
            ...screen.layout,
            children: nextChildren,
          },
        };
      }

      return screen;
    })
    .filter((screen) => {
      if (screen && screen.id === readMoreScreenId) {
        hasChanges = true;
        return false;
      }
      return true;
    });

  return {
    screens: cleanedScreens,
    changed: hasChanges,
  };
}

const FlowJSONEditor = ({
  initialFlowJSON,
  onFlowChange,
  onActiveScreenChange,
  activeScreenId,
  brandId,
  flowId,
  fieldMappings = {},
  onFieldMappingsChange,
}) => {
  // State management
  const [flowData, rawSetFlowData] = useState(() =>
    normalizeFlowJSON(initialFlowJSON || getDefaultFlowJSON()),
  );

  const setFlowData = useCallback(
    (updater) => {
      rawSetFlowData((prev) => {
        const nextValue =
          typeof updater === 'function' ? updater(prev) : updater;

        if (nextValue === prev) {
          return prev;
        }

        const normalized = normalizeFlowJSON(nextValue);
        return {
          ...normalized,
          screens: autoUpdateFooterNavigation(normalized.screens),
        };
      });
    },
    [rawSetFlowData],
  );
  const [activeScreen, setActiveScreen] = useState(null);
  const [isAddComponentModalVisible, setIsAddComponentModalVisible] =
    useState(false);
  const [crmAttributes, setCrmAttributes] = useState([]);
  const [crmAttributesLoading, setCrmAttributesLoading] = useState(false);
  const screens = flowData?.screens ?? [];
  const previousExternalScreenIdRef = useRef(activeScreenId);
  const READ_MORE_PREFIX = 'READ_MORE';

  const getReadMoreScreenIdFromComponent = (component) => {
    const action = component?.['on-click-action'];
    if (
      action?.name === 'navigate' &&
      action?.next?.type === 'screen' &&
      action?.next?.name
    ) {
      return action.next.name;
    }
    return null;
  };

  useEffect(() => {
    if (!brandId) {
      setCrmAttributes([]);
      setCrmAttributesLoading(false);
      return;
    }

    let isCancelled = false;
    const blockedContactAttributes = new Set(['country_code', 'mobile_number']);

    const loadAttributes = async () => {
      setCrmAttributesLoading(true);
      try {
        const res = await AttributeList(brandId);
        const attributesData = res?.data?.scopes_and_attributes_list || [];
        const allAttributes = [];

        attributesData.forEach((scope) => {
          if (scope?.profile_attributes?.length) {
            scope.profile_attributes.forEach((attr) => {
              const isContactInfoScope =
                typeof scope.name === 'string' &&
                scope.name.toLowerCase() === 'contact_information';

              if (
                isContactInfoScope &&
                blockedContactAttributes.has(attr.name)
              ) {
                return;
              }

              allAttributes.push({
                id: attr.name,
                name: attr.name,
                label: attr.label,
                type: attr.attribute_type,
                scope: scope.name,
                scopeId: scope.id,
              });
            });
          }
        });

        if (!isCancelled) {
          setCrmAttributes(allAttributes);
        }
      } catch (error) {
        if (!isCancelled) {
          console.error('Failed to load CRM attributes:', error);
          message.error('Failed to load CRM attributes');
          setCrmAttributes([]);
        }
      } finally {
        if (!isCancelled) {
          setCrmAttributesLoading(false);
        }
      }
    };

    loadAttributes();

    return () => {
      isCancelled = true;
    };
  }, [brandId]);

  const handleCrmFieldChange = useCallback(
    (fieldId, crmFieldName, screenIndex = null) => {
      if (!fieldId || typeof onFieldMappingsChange !== 'function') return;

      // Use screen prefix if provided
      const mappingKey =
        screenIndex !== null ? `${screenIndex}_${fieldId}` : fieldId;

      onFieldMappingsChange((prev = {}) => {
        const next = { ...prev };

        if (!crmFieldName) {
          // Clear both prefixed and legacy keys to be safe
          const hasPrefixed = !!next[mappingKey];
          const hasLegacy = !!next[fieldId];

          if (!hasPrefixed && !hasLegacy) return prev;

          delete next[mappingKey];
          delete next[fieldId];
          return next;
        }

        if (next[mappingKey] === crmFieldName) {
          return prev;
        }

        next[mappingKey] = crmFieldName;
        return next;
      });
    },
    [onFieldMappingsChange],
  );

  const removeMappingsForFields = useCallback(
    (fieldIds = [], screenIndex = null) => {
      if (
        typeof onFieldMappingsChange !== 'function' ||
        !Array.isArray(fieldIds)
      ) {
        return;
      }

      onFieldMappingsChange((prev = {}) => {
        let changed = false;
        const next = { ...prev };

        fieldIds.forEach((fieldId) => {
          const prefixedKey =
            screenIndex !== null ? `${screenIndex}_${fieldId}` : null;

          if (prefixedKey && next[prefixedKey]) {
            delete next[prefixedKey];
            changed = true;
          }

          if (next[fieldId]) {
            delete next[fieldId];
            changed = true;
          }
        });

        return changed ? next : prev;
      });
    },
    [onFieldMappingsChange],
  );

  const moveMappingOnRename = useCallback(
    (oldId, newId, screenIndex = null) => {
      if (
        !oldId ||
        !newId ||
        oldId === newId ||
        typeof onFieldMappingsChange !== 'function'
      ) {
        return;
      }

      onFieldMappingsChange((prev = {}) => {
        const oldPrefixedKey =
          screenIndex !== null ? `${screenIndex}_${oldId}` : null;
        const newPrefixedKey =
          screenIndex !== null ? `${screenIndex}_${newId}` : null;

        const next = { ...prev };
        let changed = false;

        // Move prefixed mapping if exists
        if (oldPrefixedKey && next[oldPrefixedKey]) {
          const value = next[oldPrefixedKey];
          delete next[oldPrefixedKey];
          next[newPrefixedKey] = value;
          changed = true;
        }

        // Move legacy mapping if exists
        if (next[oldId]) {
          const value = next[oldId];
          delete next[oldId];
          next[newId] = value;
          changed = true;
        }

        return changed ? next : prev;
      });
    },
    [onFieldMappingsChange],
  );

  const getFieldNamesFromComponent = useCallback(
    (component) => extractFormFields({ layout: { children: [component] } }),
    [],
  );

  // finding read more screen
  // const isReadMoreScreen = useCallback(
  //   (screen) => {
  //     return Boolean(screen?.__meta?.isOptInReadMore);
  //   },
  //   []
  // );
  // It returns true if ANY screen contains an OptIn whose on-click-action.next.name
  // points to the given screen (i.e., the given screen is a "Read more" page).

  const isReadMoreScreen = useCallback(
    (screen) => {
      const targetId = typeof screen === 'string' ? screen : screen?.id;
      if (!targetId || !Array.isArray(screens)) return false;

      const traverse = (node) => {
        if (!node) return false;

        // Arrays: check any child
        if (Array.isArray(node)) {
          return node.some(traverse);
        }

        // Objects: check this node, then descend
        if (typeof node === 'object') {
          // ✅ Check for OptIn -> on-click-action.next.name === targetId
          if (
            node.type === 'OptIn' &&
            node['on-click-action']?.next?.name === targetId
          ) {
            return true;
          }

          // Common WhatsApp Flow nesting
          if (node.layout && traverse(node.layout)) return true;
          if (node.children && traverse(node.children)) return true;

          // Be robust: scan any other nested objects/arrays too
          for (const key in node) {
            if (key === 'layout' || key === 'children') continue;
            const val = node[key];
            if (val && (typeof val === 'object' || Array.isArray(val))) {
              if (traverse(val)) return true;
            }
          }
        }

        return false;
      };

      // Look across ALL screens to find an OptIn linking to this targetId
      for (const s of screens) {
        if (traverse(s?.layout)) return true;
      }

      return false;
    },
    [screens],
  );

  const findParentScreenIdForNext = useCallback(
    (nextScreenId) => {
      if (!nextScreenId || !Array.isArray(screens)) return null;

      // Recursively check if any node navigates to target
      const pointsToTarget = (node, targetId) => {
        if (!node) return false;

        // Arrays: any child matches
        if (Array.isArray(node))
          return node.some((child) => pointsToTarget(child, targetId));

        // Objects
        if (typeof node === 'object') {
          // Check on-click-action -> next -> name === targetId
          const nextName = node?.['on-click-action']?.next?.name;
          if (nextName === targetId) return true;

          // Usual nesting
          if (node.layout && pointsToTarget(node.layout, targetId)) return true;
          if (node.children && pointsToTarget(node.children, targetId))
            return true;

          // Scan other nested objects/arrays to be robust
          for (const key in node) {
            if (key === 'layout' || key === 'children') continue;
            const val = node[key];
            if (val && (typeof val === 'object' || Array.isArray(val))) {
              if (pointsToTarget(val, targetId)) return true;
            }
          }
        }

        return false;
      };

      // Walk each screen; the first one that points to target is the "parent"
      for (const scr of screens) {
        if (pointsToTarget(scr?.layout, nextScreenId)) {
          return scr?.id ?? null;
        }
      }

      return null;
    },
    [screens],
  );

  const findFirstMainScreenId = useCallback(
    (list) => {
      if (!Array.isArray(list)) return null;
      const mainScreen = list.find((screen) => !isReadMoreScreen(screen));
      return mainScreen?.id ?? list?.[0]?.id ?? null;
    },
    [isReadMoreScreen],
  );

  const removeReadMoreScreenById = useCallback(
    (readMoreScreenId) => {
      if (!readMoreScreenId) return;

      setFlowData((prev) => {
        const { screens: cleanedScreens, changed } =
          removeReadMoreScreenReferences(
            prev.screens,
            readMoreScreenId,
            getReadMoreScreenIdFromComponent,
          );

        if (!changed) {
          return prev;
        }

        return {
          ...prev,
          screens: cleanedScreens,
        };
      });
    },
    [getReadMoreScreenIdFromComponent, setFlowData],
  );

  // Constants based on Meta documentation
  const MAX_SCREENS = 20; // Max screens per flow
  const MAX_COMPONENTS_PER_SCREEN = 50; // Max components per screen
  const MAX_ROUTING_BRANCHES = 10; // Max routing branches per screen

  /**
   * Component type definitions with Meta specifications
   */
  const COMPONENT_CATEGORIES = {
    text: {
      label: 'Text',
      components: [
        { type: 'TextHeading', label: 'Large Heading', icon: 'H1' },
        { type: 'TextSubheading', label: 'Small Heading', icon: 'H2' },
        { type: 'TextCaption', label: 'Caption', icon: 'Cc' },
        { type: 'TextBody', label: 'Body', icon: 'T' },
      ],
    },
    media: {
      label: 'Media',
      components: [{ type: 'Image', label: 'Image', icon: '📷' }],
    },
    input: {
      label: 'Text Answer',
      components: [
        { type: 'TextInput', label: 'Short Answer', icon: '📝' },
        { type: 'TextArea', label: 'Paragraph', icon: '📄' },
        { type: 'DatePicker', label: 'Date Picker', icon: '📅' },
      ],
    },
    selection: {
      label: 'Selection',
      components: [
        { type: 'RadioButtonsGroup', label: 'Single Choice', icon: '⭕' },
        { type: 'CheckboxGroup', label: 'Multiple Choice', icon: '☑️' },
        { type: 'Dropdown', label: 'Dropdown', icon: '▼' },
      ],
    },
    consent: {
      label: 'Consent',
      components: [{ type: 'OptIn', label: 'Opt-in', icon: '✅' }],
    },
    structure: {
      label: 'Structure',
      components: [{ type: 'Form', label: 'Form', icon: '🧾' }],
    },
  };

  /**
   * Removes internal metadata properties that should not be sent to Meta
   * - Strips `meta` property from screens
   * - Strips `__meta` from any component
   * - Removes empty data objects
   * - Removes unnecessary properties
   */
  const cleanFlowJSON = useCallback((flowJSON) => {
    if (!flowJSON || typeof flowJSON !== 'object') return flowJSON;

    const cleanedScreens = (flowJSON.screens || []).map((screen) => {
      if (!screen) return screen;

      // Remove meta property
      const { meta, ...screenWithoutMeta } = screen;

      // Clean data object - remove if empty
      const cleanedData =
        screen.data && Object.keys(screen.data).length > 0 ? screen.data : {};

      // Clean layout children recursively
      const cleanChildren = (children) => {
        if (!Array.isArray(children)) return children;

        return children.map((child) => {
          if (!child || typeof child !== 'object') return child;

          // Remove __meta if exists
          const { __meta, ...childWithoutMeta } = child;

          // Clean nested children
          if (Array.isArray(child.children)) {
            return {
              ...childWithoutMeta,
              children: cleanChildren(child.children),
            };
          }

          return childWithoutMeta;
        });
      };

      const cleanedLayout = screen.layout
        ? {
            ...screen.layout,
            children: cleanChildren(screen.layout.children),
          }
        : screen.layout;

      return {
        ...screenWithoutMeta,
        data: cleanedData,
        layout: cleanedLayout,
      };
    });

    return {
      ...flowJSON,
      screens: cleanedScreens,
    };
  }, []);

  /**
   * Notify parent component of flow changes
   */
  useEffect(() => {
    // Auto-update footer navigation BEFORE sending to parent
    const updatedScreens = autoUpdateFooterNavigation(flowData.screens);

    const updatedFlowData = {
      ...flowData,
      screens: updatedScreens,
    };

    if (onFlowChange) {
      const cleanedFlow = cleanFlowJSON(updatedFlowData);
      onFlowChange(cleanedFlow);
    }
  }, [flowData, onFlowChange, cleanFlowJSON, autoUpdateFooterNavigation]);

  useEffect(() => {
    if (screens.length === 0) return;

    const externalChanged =
      activeScreenId !== previousExternalScreenIdRef.current;
    previousExternalScreenIdRef.current = activeScreenId;

    if (externalChanged && activeScreenId) {
      const existsInProp = screens.some(
        (screen) => screen.id === activeScreenId,
      );
      if (existsInProp && activeScreen !== activeScreenId) {
        setActiveScreen(activeScreenId);
        return;
      }
    }

    const currentExists = screens.some((screen) => screen.id === activeScreen);
    if (!currentExists) {
      const fallbackScreen = screens[0];
      if (fallbackScreen) {
        setActiveScreen(fallbackScreen.id);
        if (onActiveScreenChange) {
          onActiveScreenChange({
            screenId: fallbackScreen.id,
            index: 0,
            screen: fallbackScreen,
          });
        }
      }
    }
  }, [activeScreenId, screens, activeScreen, onActiveScreenChange]);

  useEffect(() => {
    if (!activeScreen || !onActiveScreenChange) return;
    const index = screens.findIndex((screen) => screen.id === activeScreen);
    if (index === -1) return;

    onActiveScreenChange({
      screenId: activeScreen,
      index,
      screen: screens[index],
    });
  }, [activeScreen, screens, onActiveScreenChange]);

  /**
   * Add a new screen to the flow
   */
  const handleAddScreen = useCallback(() => {
    if (flowData.screens.length >= MAX_SCREENS) {
      message.error(`Maximum ${MAX_SCREENS} screens allowed per flow`);
      return;
    }

    const existingIds = new Set(flowData.screens.map((screen) => screen.id));
    const newScreenId = createUniqueAlphaId('SCREEN_', existingIds);

    // Create default TextHeading component
    const defaultHeading = {
      type: 'TextHeading',
      text: 'Large Heading',
    };

    // Create Form with TextHeading inside
    const defaultForm = {
      ...getDefaultComponentProps('Form'),
      children: [
        defaultHeading, // Add heading as first child
        ...getDefaultComponentProps('Form').children, // Include existing Footer
      ],
    };

    const newScreen = {
      id: newScreenId,
      title: `Question ${flowData.screens.length + 1} of ${
        flowData.screens.length + 1
      }`,
      terminal: false,
      data: {},
      layout: {
        type: 'SingleColumnLayout',
        children: [defaultForm],
      },
    };

    setFlowData((prev) => ({
      ...prev,
      screens: [...prev.screens, newScreen],
    }));

    setActiveScreen(newScreenId);
    message.success('Screen added successfully');
  }, [flowData.screens, setFlowData]);

  const handleAddOptInReadMore = useCallback(
    ({ screenId, componentIndex, formIndex = null }) => {
      if (!screenId || typeof componentIndex !== 'number') {
        return;
      }

      let didUpdate = false;
      let readMoreScreenId = null;

      setFlowData((prev) => {
        const screenIdx = prev.screens.findIndex(
          (screen) => screen.id === screenId,
        );
        if (screenIdx === -1) return prev;

        const targetScreen = prev.screens[screenIdx];
        const layoutChildren = Array.isArray(targetScreen.layout?.children)
          ? targetScreen.layout.children
          : [];

        const resolveTargetComponent = () => {
          if (formIndex == null) {
            return layoutChildren[componentIndex];
          }

          const formComponent = layoutChildren[formIndex];
          const formChildren = Array.isArray(formComponent?.children)
            ? formComponent.children
            : [];
          return formChildren[componentIndex];
        };

        const optInComponent = resolveTargetComponent();
        if (!optInComponent || optInComponent.type !== 'OptIn') {
          return prev;
        }

        const existingIds = new Set(prev.screens.map((screen) => screen.id));
        readMoreScreenId = createUniqueAlphaId(READ_MORE_PREFIX, existingIds);

        const existingReadMoreId =
          getReadMoreScreenIdFromComponent(optInComponent);
        if (existingReadMoreId) {
          readMoreScreenId = existingReadMoreId;
          return prev;
        }

        const readMoreScreen = {
          id: readMoreScreenId,
          title: 'Read more',
          terminal: false,
          data: {},
          layout: {
            type: 'SingleColumnLayout',
            children: [
              {
                type: 'TextBody',
                text: 'Add supporting information for this opt-in.',
              },
            ],
          },
        };

        const updatedOptIn = {
          ...optInComponent,
          'on-click-action': {
            name: 'navigate',
            next: { type: 'screen', name: readMoreScreenId },
            payload: {},
          },
        };

        const updatedLayoutChildren = layoutChildren.map((component, idx) => {
          if (formIndex == null) {
            if (idx !== componentIndex) {
              return component;
            }
            didUpdate = true;
            return updatedOptIn;
          }

          if (idx !== formIndex) {
            return component;
          }

          const formChildren = Array.isArray(component.children)
            ? component.children
            : [];
          const nextChildren = formChildren.map((child, childIdx) =>
            childIdx === componentIndex ? updatedOptIn : child,
          );

          didUpdate = true;

          return {
            ...component,
            children: nextChildren,
          };
        });

        if (!didUpdate) {
          return prev;
        }

        const updatedScreens = [...prev.screens];
        updatedScreens[screenIdx] = {
          ...targetScreen,
          layout: {
            ...targetScreen.layout,
            children: updatedLayoutChildren,
          },
        };

        updatedScreens.splice(screenIdx + 1, 0, readMoreScreen);

        return {
          ...prev,
          screens: updatedScreens,
        };
      });

      if (didUpdate) {
        setActiveScreen(readMoreScreenId);
        message.success('"Read more" screen added');
      } else {
        readMoreScreenId = null;
      }
    },
    [READ_MORE_PREFIX, getReadMoreScreenIdFromComponent, setFlowData],
  );

  const handleRemoveOptInReadMore = useCallback(
    ({ readMoreScreenId, parentScreenId }) => {
      if (!readMoreScreenId) return;

      Modal.confirm({
        title: 'Remove "Read more" screen',
        content: 'Are you sure you want to remove this "Read more" screen?',
        okText: 'Remove',
        okButtonProps: { danger: true },
        onOk: () => {
          removeReadMoreScreenById(readMoreScreenId);
          setActiveScreen((prevActive) =>
            prevActive === readMoreScreenId
              ? parentScreenId ?? prevActive
              : prevActive,
          );
          message.success('"Read more" screen removed');
        },
      });
    },
    [removeReadMoreScreenById],
  );

  const handleEditOptInReadMore = useCallback(
    (readMoreScreenId) => {
      if (!readMoreScreenId) return;
      const exists = flowData.screens.some(
        (screen) => screen.id === readMoreScreenId,
      );
      if (!exists) {
        message.error('The "Read more" screen could not be found.');
        return;
      }
      setActiveScreen(readMoreScreenId);
    },
    [flowData.screens],
  );

  const getReadMoreScreenDetails = useCallback(
    (screenId) => flowData.screens.find((screen) => screen.id === screenId),
    [flowData.screens],
  );

  /**
   * Delete a screen from the flow
   */
  const handleDeleteScreen = useCallback(
    (screenId) => {
      const targetScreen = flowData.screens.find(
        (screen) => screen.id === screenId,
      );
      if (!targetScreen) return;

      if (flowData.screens.length <= 1) {
        message.error('At least one screen is required');
        return;
      }

      if (isReadMoreScreen(targetScreen)) {
        message.warning(
          'Please remove "Read more" screens from their Opt-in component.',
        );
        return;
      }

      const readMoreScreensToRemove = new Set();
      (targetScreen.layout?.children ?? []).forEach((component) => {
        const readMoreId = getReadMoreScreenIdFromComponent(component);
        if (readMoreId) {
          readMoreScreensToRemove.add(readMoreId);
        }
      });

      const fieldsToRemove = new Set(extractFormFields(targetScreen));
      readMoreScreensToRemove.forEach((screenId) => {
        const readMoreScreen = flowData.screens.find((s) => s.id === screenId);
        extractFormFields(readMoreScreen).forEach((fieldId) =>
          fieldsToRemove.add(fieldId),
        );
      });

      Modal.confirm({
        title: 'Delete Screen',
        content: 'Are you sure you want to delete this screen?',
        onOk: () => {
          const remainingScreens = flowData.screens
            .filter(
              (screen) =>
                screen.id !== screenId &&
                !readMoreScreensToRemove.has(screen.id),
            )
            .map((screen) => {
              const updatedChildren = (screen.layout?.children ?? []).map(
                (component) => {
                  const relatedReadMoreId =
                    getReadMoreScreenIdFromComponent(component);
                  if (
                    relatedReadMoreId &&
                    readMoreScreensToRemove.has(relatedReadMoreId)
                  ) {
                    const updatedComponent = { ...component };
                    delete updatedComponent['on-click-action'];
                    delete updatedComponent['read-more-screen-id'];
                    return updatedComponent;
                  }
                  return component;
                },
              );

              return {
                ...screen,
                layout: {
                  ...screen.layout,
                  children: updatedChildren,
                },
              };
            });

          setFlowData((prev) => ({
            ...prev,
            screens: remainingScreens,
          }));

          // Migrate field mappings based on screen index changes
          if (typeof onFieldMappingsChange === 'function') {
            onFieldMappingsChange((prevMappings = {}) => {
              const nextMappings = {};
              const screenIdToIndex = new Map(
                remainingScreens.map((s, i) => [s.id, i]),
              );
              const oldScreens = flowData.screens;

              Object.entries(prevMappings).forEach(([key, value]) => {
                const match = key.match(/^(\d+)_(\w+)$/);
                if (match) {
                  const oldIndex = parseInt(match[1], 10);
                  const fieldName = match[2];
                  const oldScreen = oldScreens[oldIndex];

                  if (!oldScreen) return;

                  // If the screen being deleted is this one or its read-more, skip
                  if (
                    oldScreen.id === screenId ||
                    readMoreScreensToRemove.has(oldScreen.id)
                  ) {
                    return;
                  }

                  const newIndex = screenIdToIndex.get(oldScreen.id);
                  if (newIndex !== undefined) {
                    nextMappings[`${newIndex}_${fieldName}`] = value;
                  }
                } else {
                  // Keep legacy or other keys as is
                  nextMappings[key] = value;
                }
              });
              return nextMappings;
            });
          }

          const fallbackId = findFirstMainScreenId(remainingScreens);
          setActiveScreen((prevActive) => {
            if (
              prevActive === screenId ||
              readMoreScreensToRemove.has(prevActive)
            ) {
              return fallbackId;
            }
            return prevActive;
          });

          if (fieldsToRemove.size) {
            removeMappingsForFields([...fieldsToRemove]);
          }

          message.success('Screen deleted successfully');
        },
      });
    },
    [
      flowData.screens,
      getReadMoreScreenIdFromComponent,
      isReadMoreScreen,
      findFirstMainScreenId,
      removeMappingsForFields,
    ],
  );

  /**
   * Update screen properties
   */
  const handleUpdateScreen = useCallback((screenId, updates) => {
    setFlowData((prev) => ({
      ...prev,
      screens: prev.screens.map((screen) =>
        screen.id === screenId ? { ...screen, ...updates } : screen,
      ),
    }));
  }, []);

  /**
   * Add component to screen
   */
  const handleAddComponent = useCallback(
    (screenId, componentType) => {
      const screen = flowData.screens.find((s) => s.id === screenId);

      if (!screen) return;

      if (componentType === 'Form') {
        message.info('Screens already contain a form to hold content.');
        return;
      }

      if (isReadMoreScreen(screen)) {
        const allowedTypes = new Set([
          'TextHeading',
          'TextSubheading',
          'TextCaption',
          'TextBody',
          'Image',
        ]);
        if (!allowedTypes.has(componentType)) {
          message.error(
            'Only text or media components are allowed on "Read more" screens.',
          );
          return;
        }
      }

      const formIndex = Array.isArray(screen.layout?.children)
        ? screen.layout.children.findIndex((child) => child?.type === 'Form')
        : -1;

      const formChildren =
        formIndex >= 0 &&
        Array.isArray(screen.layout.children[formIndex]?.children)
          ? screen.layout.children[formIndex].children.filter(Boolean)
          : [];

      const contentCount =
        formIndex >= 0
          ? formChildren.filter((child) => child.type !== 'Footer').length
          : Array.isArray(screen.layout?.children)
          ? screen.layout.children.filter((child) => child?.type !== 'Footer')
              .length
          : 0;

      if (contentCount >= MAX_COMPONENTS_PER_SCREEN) {
        message.error(
          `Maximum ${MAX_COMPONENTS_PER_SCREEN} components allowed per screen`,
        );
        return;
      }

      if (
        componentType === 'Footer' &&
        formIndex >= 0 &&
        formChildren.some((child) => child?.type === 'Footer')
      ) {
        message.warning('Form already contains a footer');
        return;
      }

      const newComponent = getDefaultComponentProps(componentType);
      if (!newComponent) {
        message.warning('Unable to add this component');
        return;
      }

      let didUpdate = false;

      setFlowData((prev) => {
        const nextScreens = prev.screens.map((prevScreen) => {
          if (prevScreen.id !== screenId) return prevScreen;

          const layoutChildren = Array.isArray(prevScreen.layout?.children)
            ? prevScreen.layout.children
            : [];
          const nextFormIndex = layoutChildren.findIndex(
            (child) => child?.type === 'Form',
          );

          if (nextFormIndex === -1) {
            const children = [...layoutChildren];
            const footerIndex = children.findIndex(
              (child) => child?.type === 'Footer',
            );

            if (componentType === 'Footer' && footerIndex !== -1) {
              return prevScreen;
            }

            const updatedChildren =
              footerIndex !== -1
                ? [
                    ...children.slice(0, footerIndex),
                    newComponent,
                    ...children.slice(footerIndex),
                  ]
                : [...children, newComponent];

            didUpdate = true;

            return {
              ...prevScreen,
              layout: {
                ...prevScreen.layout,
                children: updatedChildren,
              },
            };
          }

          const formComponent = layoutChildren[nextFormIndex];
          const existingFormChildren = Array.isArray(formComponent.children)
            ? [...formComponent.children]
            : [];
          const footerIndex = existingFormChildren.findIndex(
            (child) => child?.type === 'Footer',
          );

          if (componentType === 'Footer' && footerIndex !== -1) {
            return prevScreen;
          }

          const updatedFormChildren = (() => {
            if (componentType === 'Footer' || footerIndex === -1) {
              return [...existingFormChildren, newComponent];
            }

            return [
              ...existingFormChildren.slice(0, footerIndex),
              newComponent,
              ...existingFormChildren.slice(footerIndex),
            ];
          })();

          didUpdate = true;

          const nextLayoutChildren = layoutChildren.map((child, idx) => {
            if (idx !== nextFormIndex) return child;
            return {
              ...child,
              children: updatedFormChildren,
            };
          });

          return {
            ...prevScreen,
            layout: {
              ...prevScreen.layout,
              children: nextLayoutChildren,
            },
          };
        });

        if (!didUpdate) {
          return prev;
        }

        return {
          ...prev,
          screens: nextScreens,
        };
      });

      if (didUpdate) {
        setIsAddComponentModalVisible(false);
        message.success('Component added successfully');
      }
    },
    [flowData.screens, isReadMoreScreen, setFlowData],
  );

  /**
   * Delete component from screen
   */
  const handleDeleteComponent = useCallback(
    (screenId, componentIndex) => {
      Modal.confirm({
        title: 'Delete Component',
        content: 'Are you sure you want to delete this component?',
        onOk: () => {
          const screen = flowData.screens.find((s) => s.id === screenId);
          const component = screen?.layout?.children?.[componentIndex];
          const readMoreScreenId = getReadMoreScreenIdFromComponent(component);
          const componentFieldIds = getFieldNamesFromComponent(component);

          setFlowData((prev) => ({
            ...prev,
            screens: prev.screens.map((screen) => {
              if (screen.id === screenId) {
                return {
                  ...screen,
                  layout: {
                    ...screen.layout,
                    children: screen.layout.children.filter(
                      (_, idx) => idx !== componentIndex,
                    ),
                  },
                };
              }
              return screen;
            }),
          }));

          if (readMoreScreenId) {
            removeReadMoreScreenById(readMoreScreenId);
            setActiveScreen((prevActive) =>
              prevActive === readMoreScreenId ? screenId : prevActive,
            );
          }

          if (componentFieldIds?.length) {
            const screenIdx = flowData.screens.findIndex(
              (s) => s.id === screenId,
            );
            removeMappingsForFields(
              componentFieldIds,
              screenIdx !== -1 ? screenIdx : null,
            );
          }

          message.success('Component deleted successfully');
        },
      });
    },
    [
      flowData.screens,
      getFieldNamesFromComponent,
      removeReadMoreScreenById,
      removeMappingsForFields,
      getReadMoreScreenIdFromComponent,
    ],
  );

  /**
   * Update component properties
   */
  const handleUpdateComponent = useCallback(
    (screenId, componentIndex, updates) => {
      setFlowData((prev) => ({
        ...prev,
        screens: prev.screens.map((screen) => {
          if (screen.id === screenId) {
            return {
              ...screen,
              layout: {
                ...screen.layout,
                children: screen.layout.children.map((component, idx) =>
                  idx === componentIndex
                    ? { ...component, ...updates }
                    : component,
                ),
              },
            };
          }
          return screen;
        }),
      }));
    },
    [],
  );

  /**
   * Removes empty string properties from component to comply with Meta validation
   * Properties like helper-text, min-date, max-date should not be empty strings
   */
  function removeEmptyStringProperties(component) {
    if (!component || typeof component !== 'object') {
      return component;
    }

    const cleaned = { ...component };

    // List of optional properties that should not be empty strings
    const optionalProps = [
      'helper-text',
      'min-date',
      'max-date',
      'error-message',
    ];

    optionalProps.forEach((prop) => {
      if (cleaned[prop] === '') {
        delete cleaned[prop];
      }
    });

    // Handle nested children
    if (Array.isArray(cleaned.children)) {
      cleaned.children = cleaned.children.map((child) =>
        removeEmptyStringProperties(child),
      );
    }

    return cleaned;
  }

  const generateFieldKey = (label, name) => {
    if (!label && !name) return '';

    // Extract timestamp (13-digit number) from end of name
    const timestampMatch = name?.match(/_(\d{13})$/);
    const timestamp = timestampMatch ? timestampMatch[1] : '';

    // Sanitize label: lowercase, replace spaces/special chars with underscores
    const sanitizedLabel = (label || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_') // replace non-alphanumeric with underscores
      .replace(/^_+|_+$/g, ''); // trim leading/trailing underscores

    // Return "label_timestamp" or just "label"
    return timestamp ? `${sanitizedLabel}_${timestamp}` : sanitizedLabel;
  };

  /**
   * Auto-updates field name when label changes
   */
  function syncFieldNameWithLabel(component, updates) {
    // Only update name for interactive components
    const interactiveTypes = [
      'TextInput',
      'TextArea',
      'DatePicker',
      'CheckboxGroup',
      'RadioButtonsGroup',
      'Dropdown',
      'OptIn',
    ];

    if (!interactiveTypes.includes(component.type)) {
      return updates;
    }

    // // If label is being updated, also update the name
    if (updates.label !== undefined) {
      const newName = generateFieldKey(updates.label, component.name);
      if (newName) {
        return {
          ...updates,
          name: newName,
        };
      }
    }

    return updates;
  }

  const handleUpdateFormComponent = useCallback(
    (screenId, formIndex, childIndex, updates) => {
      setFlowData((prev) => ({
        ...prev,
        screens: prev.screens.map((screen) => {
          if (screen.id !== screenId) return screen;

          const updatedChildren = screen.layout.children.map(
            (component, idx) => {
              if (idx !== formIndex) return component;

              const formChildren = Array.isArray(component.children)
                ? component.children
                : [];
              const nextChildren = formChildren.map((child, cIdx) => {
                if (cIdx !== childIndex) return child;

                // ✅ This is where syncFieldNameWithLabel is called
                const syncedUpdates = syncFieldNameWithLabel(child, updates);
                const candidateName = syncedUpdates.name ?? child.name;
                let finalUpdates = syncedUpdates;

                if (
                  syncedUpdates.name &&
                  isNameTakenInForm(formChildren, candidateName, childIndex)
                ) {
                  message.error(
                    'Each field name must be unique within the same screen.',
                  );
                  const { name, ...rest } = finalUpdates;
                  finalUpdates = rest;
                }

                const originalName = child?.name;
                const updatedChild = {
                  ...child,
                  ...finalUpdates,
                };
                const cleanedChild = removeEmptyStringProperties(updatedChild);

                if (
                  originalName &&
                  cleanedChild?.name &&
                  originalName !== cleanedChild.name
                ) {
                  const screenIdx = prev.screens.findIndex(
                    (s) => s.id === screenId,
                  );
                  moveMappingOnRename(
                    originalName,
                    cleanedChild.name,
                    screenIdx !== -1 ? screenIdx : null,
                  );
                }

                return cleanedChild;
              });

              return {
                ...component,
                children: nextChildren,
              };
            },
          );

          return {
            ...screen,
            layout: {
              ...screen.layout,
              children: updatedChildren,
            },
          };
        }),
      }));
    },
    [moveMappingOnRename, setFlowData],
  );

  const handleDeleteFormComponent = useCallback(
    (screenId, formIndex, childIndex) => {
      let readMoreScreenId = null;
      let didRemove = false;
      let removedFieldId = null;

      setFlowData((prev) => {
        const nextScreens = prev.screens.map((screen) => {
          if (screen.id !== screenId) return screen;

          const layoutChildren = Array.isArray(screen.layout?.children)
            ? screen.layout.children
            : [];

          const nextChildren = layoutChildren.map((component, idx) => {
            if (idx !== formIndex) return component;

            const formChildren = Array.isArray(component.children)
              ? component.children
              : [];
            const targetChild =
              childIndex >= 0 && childIndex < formChildren.length
                ? formChildren[childIndex]
                : null;

            if (!readMoreScreenId && targetChild) {
              const candidateId = getReadMoreScreenIdFromComponent(targetChild);
              if (candidateId) {
                readMoreScreenId = candidateId;
              }
            }
            if (!removedFieldId && targetChild?.name) {
              removedFieldId = targetChild.name;
            }

            const filteredChildren = formChildren.filter(
              (_, cIdx) => cIdx !== childIndex,
            );
            if (filteredChildren.length === formChildren.length) {
              return component;
            }

            didRemove = true;

            return {
              ...component,
              children: filteredChildren,
            };
          });

          if (!didRemove) {
            return screen;
          }

          return {
            ...screen,
            layout: {
              ...screen.layout,
              children: nextChildren,
            },
          };
        });

        if (!didRemove) {
          return prev;
        }

        if (!readMoreScreenId) {
          return {
            ...prev,
            screens: nextScreens,
          };
        }

        const { screens: cleanedScreens } = removeReadMoreScreenReferences(
          nextScreens,
          readMoreScreenId,
          getReadMoreScreenIdFromComponent,
        );

        return {
          ...prev,
          screens: cleanedScreens,
        };
      });

      if (didRemove) {
        if (readMoreScreenId) {
          setActiveScreen((prevActive) =>
            prevActive === readMoreScreenId ? screenId : prevActive,
          );
        }
        message.success('Component deleted successfully');
        if (removedFieldId) {
          const screenIdx = flowData.screens.findIndex(
            (s) => s.id === screenId,
          );
          removeMappingsForFields(
            [removedFieldId],
            screenIdx !== -1 ? screenIdx : null,
          );
        }
      }
    },
    [
      getReadMoreScreenIdFromComponent,
      removeMappingsForFields,
      setActiveScreen,
      setFlowData,
    ],
  );

  const handleAddFormComponent = useCallback(
    (screenId, formIndex, componentType) => {
      if (!componentType) return;
      if (!FORM_ALLOWED_COMPONENT_TYPES.includes(componentType)) {
        message.warning('Unsupported form component');
        return;
      }

      const newComponent = getDefaultComponentProps(componentType);
      if (!newComponent) {
        message.warning('Unable to add this component');
        return;
      }

      let didUpdate = false;
      let duplicateFooter = false;

      setFlowData((prev) => {
        const nextScreens = prev.screens.map((screen) => {
          if (screen.id !== screenId) return screen;

          const nextChildren = screen.layout.children.map((component, idx) => {
            if (idx !== formIndex) return component;

            const formChildren = Array.isArray(component.children)
              ? [...component.children]
              : [];

            if (
              componentType === 'Footer' &&
              formChildren.some((child) => child.type === 'Footer')
            ) {
              duplicateFooter = true;
              return component;
            }

            let updatedChildren = formChildren;

            if (componentType !== 'Footer') {
              const footerIndex = formChildren.findIndex(
                (child) => child.type === 'Footer',
              );
              updatedChildren =
                footerIndex >= 0
                  ? [
                      ...formChildren.slice(0, footerIndex),
                      newComponent,
                      ...formChildren.slice(footerIndex),
                    ]
                  : [...formChildren, newComponent];
            } else {
              updatedChildren = [...formChildren, newComponent];
            }

            didUpdate = true;

            return {
              ...component,
              children: updatedChildren,
            };
          });

          return {
            ...screen,
            layout: {
              ...screen.layout,
              children: nextChildren,
            },
          };
        });

        if (!didUpdate) {
          return prev;
        }

        return {
          ...prev,
          screens: nextScreens,
        };
      });

      if (duplicateFooter) {
        message.warning('Form already contains a footer');
      } else if (didUpdate) {
        message.success('Component added successfully');
      }
    },
    [],
  );

  const handleFormComponentDragEnd = useCallback(
    (result) => {
      const { source, destination } = result;
      if (!destination) return;

      const sourceInfo = parseFormDroppableId(source.droppableId);
      const destinationInfo = parseFormDroppableId(destination.droppableId);

      if (
        !sourceInfo ||
        !destinationInfo ||
        sourceInfo.screenId !== destinationInfo.screenId ||
        sourceInfo.formIndex !== destinationInfo.formIndex ||
        source.index === destination.index
      ) {
        return;
      }

      setFlowData((prev) => ({
        ...prev,
        screens: prev.screens.map((screen) => {
          if (screen.id !== sourceInfo.screenId) return screen;

          const updatedChildren = screen.layout.children.map(
            (component, idx) => {
              if (idx !== sourceInfo.formIndex) return component;

              const formChildren = Array.isArray(component.children)
                ? [...component.children]
                : [];

              if (
                source.index < 0 ||
                source.index >= formChildren.length ||
                destination.index < 0
              ) {
                return component;
              }

              const [movedChild] = formChildren.splice(source.index, 1);
              if (!movedChild) {
                return component;
              }

              const targetIndex = Math.min(
                destination.index,
                formChildren.length,
              );
              formChildren.splice(targetIndex, 0, movedChild);

              const nonFooterChildren = formChildren.filter(
                (child) => child?.type !== 'Footer',
              );
              const footerChildren = formChildren.filter(
                (child) => child?.type === 'Footer',
              );
              const normalizedChildren =
                footerChildren.length > 0
                  ? [...nonFooterChildren, ...footerChildren]
                  : nonFooterChildren;

              return {
                ...component,
                children: normalizedChildren,
              };
            },
          );

          return {
            ...screen,
            layout: {
              ...screen.layout,
              children: updatedChildren,
            },
          };
        }),
      }));
    },
    [setFlowData],
  );

  const computeScreenGroups = useCallback(
    (inputScreens) => {
      const groups = [];
      let currentGroup = null;

      inputScreens.forEach((screen) => {
        if (!screen) {
          return;
        }

        if (!isReadMoreScreen(screen)) {
          currentGroup = { mainScreen: screen, readMoreScreens: [] };
          groups.push(currentGroup);
          return;
        }

        // const parentId = screen.__meta?.parentScreenId;
        const parentId = findParentScreenIdForNext(screen.id);

        let targetGroup = null;

        if (parentId) {
          targetGroup =
            groups.find((group) => group.mainScreen.id === parentId) || null;
        }

        if (!targetGroup) {
          targetGroup = currentGroup || null;
        }

        if (!targetGroup) {
          const fallbackGroup = { mainScreen: screen, readMoreScreens: [] };
          groups.push(fallbackGroup);
          currentGroup = fallbackGroup;
          return;
        }

        targetGroup.readMoreScreens.push(screen);
      });

      return groups;
    },
    [isReadMoreScreen],
  );

  /**
   * Handle drag and drop reordering
   */
  const handleComponentDragEnd = useCallback(
    (result) => {
      if (!result.destination) return;

      const { source, destination } = result;
      const screenId = activeScreen;
      const screen = flowData.screens.find((s) => s.id === screenId);

      if (!screen) return;

      const children = Array.from(screen.layout.children);
      const footerIndex = children.findIndex((c) => c.type === 'Footer');

      // Prevent dragging footer
      if (source.index === footerIndex || destination.index === footerIndex) {
        message.warning('Footer cannot be moved');
        return;
      }

      const [removed] = children.splice(source.index, 1);
      children.splice(destination.index, 0, removed);

      setFlowData((prev) => ({
        ...prev,
        screens: prev.screens.map((s) =>
          s.id === screenId ? { ...s, layout: { ...s.layout, children } } : s,
        ),
      }));
    },
    [activeScreen, flowData.screens],
  );

  /**
   * Handle screens drag and drop reordering
   */
  const handleScreenDragEnd = useCallback(
    (result) => {
      if (!result.destination) return;

      const { source, destination } = result;

      if (source.index === destination.index) {
        return;
      }

      setFlowData((prev) => {
        const oldScreens = prev.screens;
        const groups = computeScreenGroups(oldScreens);

        if (
          source.index < 0 ||
          source.index >= groups.length ||
          destination.index < 0 ||
          destination.index > groups.length
        ) {
          return prev;
        }

        const [movedGroup] = groups.splice(source.index, 1);
        if (!movedGroup) {
          return prev;
        }

        groups.splice(destination.index, 0, movedGroup);

        const updatedScreens = groups.flatMap((group) => [
          group.mainScreen,
          ...group.readMoreScreens,
        ]);

        // Migrate field mappings based on screen index changes
        if (typeof onFieldMappingsChange === 'function') {
          onFieldMappingsChange((prevMappings = {}) => {
            const nextMappings = {};
            const screenIdToIndex = new Map(
              updatedScreens.map((s, i) => [s.id, i]),
            );
            const oldScreenIdToIndex = new Map(
              oldScreens.map((s, i) => [s.id, i]),
            );

            Object.entries(prevMappings).forEach(([key, value]) => {
              const match = key.match(/^(\d+)_(\w+)$/);
              if (match) {
                const oldIndex = parseInt(match[1], 10);
                const fieldName = match[2];
                const screenId = oldScreens[oldIndex]?.id;
                const newIndex = screenIdToIndex.get(screenId);

                if (newIndex !== undefined) {
                  nextMappings[`${newIndex}_${fieldName}`] = value;
                }
                // If screen was deleted or not found, mapping is dropped
              } else {
                // Keep legacy or other keys as is
                nextMappings[key] = value;
              }
            });
            return nextMappings;
          });
        }

        const fallbackId = findFirstMainScreenId(updatedScreens);
        setActiveScreen((prevActive) => {
          if (!prevActive) {
            return fallbackId;
          }

          return updatedScreens.some((screen) => screen.id === prevActive)
            ? prevActive
            : fallbackId;
        });

        return {
          ...prev,
          screens: updatedScreens,
        };
      });
    },
    [computeScreenGroups, findFirstMainScreenId, setFlowData],
  );

  /**
   * Combined drag handler for screens and components
   */
  const handleDragEnd = useCallback(
    (result) => {
      if (!result.destination) return;

      if (
        result.type === 'SCREEN' ||
        result.source.droppableId === 'screens' ||
        result.destination.droppableId === 'screens'
      ) {
        handleScreenDragEnd(result);
        return;
      }

      if (
        result.type === 'FORM_COMPONENT' ||
        (result.source.droppableId.startsWith('form::') &&
          result.destination.droppableId.startsWith('form::'))
      ) {
        handleFormComponentDragEnd(result);
        return;
      }

      handleComponentDragEnd(result);
    },
    [handleComponentDragEnd, handleScreenDragEnd, handleFormComponentDragEnd],
  );

  const formComponentHelpers = {
    handleAddFormComponent,
    handleUpdateFormComponent,
    handleDeleteFormComponent,
    createFormDroppableId,
    createFormChildDraggableId,
  };

  const optInHelpers = {
    addReadMoreScreen: handleAddOptInReadMore,
    removeReadMoreScreen: handleRemoveOptInReadMore,
    editReadMoreScreen: handleEditOptInReadMore,
    getReadMoreScreen: getReadMoreScreenDetails,
  };

  const screenGroups = useMemo(
    () => computeScreenGroups(screens),
    [computeScreenGroups, screens],
  );

  const activeScreenData = screens.find((screen) => screen.id === activeScreen);
  const addComponentCategories = (() => {
    const filterFormComponent = (source) =>
      Object.entries(source).reduce((acc, [key, category]) => {
        const filteredComponents = category.components.filter(
          (component) => component.type !== 'Form',
        );

        if (filteredComponents.length === 0) {
          return acc;
        }

        acc[key] = {
          ...category,
          components: filteredComponents,
        };

        return acc;
      }, {});

    if (!isReadMoreScreen(activeScreenData)) {
      return filterFormComponent(COMPONENT_CATEGORIES);
    }

    const allowedKeys = ['text', 'media'];
    const subset = allowedKeys.reduce((acc, key) => {
      if (COMPONENT_CATEGORIES[key]) {
        acc[key] = COMPONENT_CATEGORIES[key];
      }
      return acc;
    }, {});

    return filterFormComponent(subset);
  })();

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      {/* OUTER WRAPPER — single scroll container */}
      <div className="flex h-full min-h-0 flex-col rounded-2xl bg-white shadow-sm w-full overflow-y-auto">
        {/* ===== SCREEN LIST SECTION ===== */}
        <section className="flex flex-col border-b border-gray-100 px-6 py-5">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                Screens
              </p>
              <h2 className="mt-1 text-base font-semibold text-gray-800">
                Flow structure
              </h2>
              <p className="text-xs text-gray-500">
                Drag to reorder and click to edit a screen.
              </p>
            </div>
          </div>

          <StrictModeDroppable
            droppableId="screens"
            type="SCREEN"
            direction="vertical"
          >
            {(provided) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className="mt-4 flex flex-col gap-2"
              >
                {screenGroups.map(
                  ({ mainScreen, readMoreScreens = [] }, index) => {
                    const mainIsActive = activeScreen === mainScreen.id;

                    return (
                      <Draggable
                        key={mainScreen.id}
                        draggableId={`screen-group-${mainScreen.id}`}
                        index={index}
                      >
                        {(dragProvided, snapshot) => {
                          const mainRowClasses = [
                            'flex items-center justify-between rounded-lg border px-4 py-3 transition-colors cursor-pointer',
                            snapshot.isDragging || mainIsActive
                              ? 'border-blue-200 bg-blue-50 shadow-sm'
                              : 'border-transparent hover:border-blue-200 hover:bg-gray-50',
                          ].join(' ');

                          const handleMainClick = () =>
                            setActiveScreen(mainScreen.id);

                          return (
                            <div
                              ref={dragProvided.innerRef}
                              {...dragProvided.draggableProps}
                              className="flex flex-col gap-2"
                            >
                              <div
                                className={mainRowClasses}
                                onClick={handleMainClick}
                                role="button"
                                tabIndex={0}
                                {...dragProvided.dragHandleProps}
                              >
                                <div className="flex items-center gap-3">
                                  <span className="flex h-6 w-6 items-center justify-center text-gray-500">
                                    <HolderOutlined />
                                  </span>
                                  <div className="text-left">
                                    <p className="text-sm font-semibold text-gray-800">
                                      {mainScreen.title ||
                                        `Screen ${index + 1}`}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                      Screen ID: {mainScreen.id}
                                    </p>
                                  </div>
                                </div>

                                <div className="flex items-center gap-2">
                                  <Tooltip title="Copy Screen ID">
                                    <button
                                      type="button"
                                      className="rounded-md border border-transparent p-1.5 text-gray-600 transition hover:border-blue-200 hover:bg-white"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        navigator.clipboard.writeText(
                                          mainScreen.id,
                                        );
                                        message.success('Screen ID copied');
                                      }}
                                    >
                                      <CopyOutlined />
                                    </button>
                                  </Tooltip>
                                  <button
                                    type="button"
                                    className="rounded-md border border-transparent p-1.5 text-red-500 transition hover:border-red-200 hover:bg-white disabled:opacity-40"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteScreen(mainScreen.id);
                                    }}
                                    disabled={screenGroups.length <= 1}
                                  >
                                    <CustomDelete color="currentColor" />
                                  </button>
                                </div>
                              </div>

                              {readMoreScreens.length > 0 && (
                                <div className="ml-6 flex flex-col gap-1">
                                  {readMoreScreens.map((readScreen) => (
                                    <button
                                      type="button"
                                      key={readScreen.id}
                                      className={`flex w-full items-center justify-between rounded-md px-3 py-2 text-sm transition-colors ${
                                        activeScreen === readScreen.id
                                          ? 'bg-blue-50 text-gray-800'
                                          : 'text-gray-600 hover:bg-gray-100'
                                      }`}
                                      onClick={() =>
                                        setActiveScreen(readScreen.id)
                                      }
                                    >
                                      <div className="flex items-center gap-2">
                                        <span className="text-gray-300">
                                          ::
                                        </span>
                                        <div className="text-left">
                                          <p className="text-xs font-semibold">
                                            {readScreen.title || 'Read more'}
                                          </p>
                                          <p className="text-[11px] text-gray-500">
                                            Screen ID: {readScreen.id}
                                          </p>
                                        </div>
                                      </div>
                                      <Tooltip title="Copy Screen ID">
                                        <span
                                          role="button"
                                          tabIndex={0}
                                          className="rounded-md border border-transparent p-1 text-gray-500 transition hover:border-blue-200 hover:bg-white"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            navigator.clipboard.writeText(
                                              readScreen.id,
                                            );
                                            message.success('Screen ID copied');
                                          }}
                                        >
                                          <CopyOutlined />
                                        </span>
                                      </Tooltip>
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        }}
                      </Draggable>
                    );
                  },
                )}
                {provided.placeholder}
              </div>
            )}
          </StrictModeDroppable>

          <button
            type="button"
            onClick={handleAddScreen}
            disabled={screens.length >= MAX_SCREENS}
            className="mt-3 text-sm font-semibold text-gray-600 transition hover:text-gray-800 disabled:cursor-not-allowed disabled:opacity-60"
          >
            + Add new screen
          </button>
          {screens.length >= MAX_SCREENS && (
            <p className="mt-2 text-xs text-gray-400">
              Maximum of {MAX_SCREENS} screens reached.
            </p>
          )}
        </section>

        {/* ===== ACTIVE SCREEN SECTION ===== */}
        <section className="flex-1 min-h-0 w-full">
          {activeScreenData ? (
            <div className="flex h-full min-h-0 flex-col w-full">
              <div className="border-b border-gray-100 px-6 py-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {activeScreenData.title || 'Untitled screen'}
                    </h3>
                    <p className="text-xs text-gray-500">
                      Configure components and branching for this step.
                    </p>
                  </div>
                  <span className="rounded-full border border-blue-200 px-3 py-1 text-xs text-gray-600">
                    Screen ID: {activeScreenData.id}
                  </span>
                </div>
              </div>

              <div className="px-6 pt-6">
                <div className="mb-[18px]">
                  <label className="mb-1 block text-sm mb-1 font-medium text-gray-700">
                    Screen title
                  </label>
                  <Input
                    value={activeScreenData.title || ''}
                    onChange={(e) =>
                      handleUpdateScreen(activeScreen, {
                        title: e.target.value,
                      })
                    }
                    placeholder="Enter screen title"
                    maxLength={60}
                    style={{ height: '38px', fontSize: '14px' }}
                  />
                  <div className="mt-1 text-xs text-gray-400">
                    {activeScreenData.title?.length || 0}/60 characters
                  </div>
                </div>

                <StrictModeDroppable
                  droppableId={`components-${activeScreen}`}
                  type="COMPONENT"
                >
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className="space-y-4"
                    >
                      {activeScreenData.layout.children.map(
                        (component, index) => (
                          <Draggable
                            key={`${activeScreen}-${index}`}
                            draggableId={`${activeScreen}-${index}`}
                            index={index}
                            isDragDisabled={
                              component.type === 'Footer' ||
                              component.type === 'Form'
                            }
                          >
                            {(dragProvided) => (
                              <div
                                ref={dragProvided.innerRef}
                                {...dragProvided.draggableProps}
                              >
                                <FlowComponentEditor
                                  component={component}
                                  componentIndex={index}
                                  screenId={activeScreen}
                                  dragHandleProps={dragProvided.dragHandleProps}
                                  onUpdate={(updates) =>
                                    handleUpdateComponent(
                                      activeScreen,
                                      index,
                                      updates,
                                    )
                                  }
                                  onDelete={() =>
                                    handleDeleteComponent(activeScreen, index)
                                  }
                                  formUtils={formComponentHelpers}
                                  optInUtils={optInHelpers}
                                  brandId={brandId}
                                  flowId={flowId}
                                  crmAttributes={crmAttributes}
                                  crmFieldMappings={fieldMappings}
                                  screenIndex={screens.findIndex(
                                    (s) => s.id === activeScreen,
                                  )}
                                  onCrmFieldChange={handleCrmFieldChange}
                                  crmAttributesLoading={crmAttributesLoading}
                                />
                              </div>
                            )}
                          </Draggable>
                        ),
                      )}
                      {provided.placeholder}
                    </div>
                  )}
                </StrictModeDroppable>

                <div className="py-4">
                  <Button
                    type="dashed"
                    icon={<PlusOutlined />}
                    onClick={() => setIsAddComponentModalVisible(true)}
                    className="flex w-full items-center justify-center gap-2"
                  >
                    Add content block
                  </Button>
                </div>
              </div>

              <div className="border-t border-gray-100 px-6 py-4">
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={handleAddScreen}
                  disabled={screens.length >= MAX_SCREENS}
                  className="flex w-full items-center justify-center gap-2"
                >
                  Add new screen
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex h-full items-center justify-center px-6 py-10 text-center text-sm text-gray-500">
              Select a screen above to start editing the flow.
            </div>
          )}
        </section>

        {/* ===== ADD COMPONENT MODAL ===== */}
        <AddComponentModal
          visible={isAddComponentModalVisible}
          onClose={() => setIsAddComponentModalVisible(false)}
          categories={addComponentCategories}
          onAddComponent={handleAddComponent}
          activeScreen={activeScreen}
        />
      </div>
    </DragDropContext>
  );
};

export default FlowJSONEditor;
